<template>
  <label :class="{ required: field.required }" :for="`select-snapshot-input-${parent}-${field.name}`">
    Select Snapshot
    <SVGIcon
      v-if="field.description"
      v-tooltip="field.description"
      :width="'20px'"
      :height="'20px'"
      :iconName="'info'"
      class="help-logo"
    />
  </label>

  <select
    :id="`select-snapshot-input-${parent}-${field.name}`"
    :value="selectValue"
    class="select"
    @input="onSnapshotSelected($event.target.value)"
  >
    <option :value="null" disabled selected>Select Snapshot</option>
    <option
      v-for="(snapshot, i) in snapshots"
      :key="`dropdown-snapshot-${parent}-${field.name}-${i}-${snapshot.id}`"
      :value="Number(snapshot.id)"
    >
      {{ getSnapshotLabel(snapshot) }}
    </option>
  </select>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';
import { usePresetSnapshot } from '@/composables/usePresetSnapshot';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';

const props = defineProps({
  parent: {
    type: String,
    default: '',
  },
  field: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: [Number, String, null],
    default: null,
  },
});

const injectedPresetSnapshotId = inject('presetSnapshotId', null);
const { presetSnapshotId: sharedPresetSnapshotId } = usePresetSnapshot();

// Prefer injected value (direct child of AppStartModal), fall back to shared composable
const presetSnapshotId = computed(() => {
  const injected = injectedPresetSnapshotId?.value ?? null;
  if (injected !== null) return injected;
  return sharedPresetSnapshotId.value;
});

const emit = defineEmits(['update:modelValue', 'update:snapshotId']);
const snapshots = ref([]);

// Use presetSnapshotId as the displayed value only when modelValue hasn't been set yet
const selectValue = computed(() => {
  const windowPreset = window.__appStartPresetSnapshotId != null ? Number(window.__appStartPresetSnapshotId) : null;
  if (props.modelValue !== null && props.modelValue !== undefined) {
    return Number(props.modelValue);
  }
  return windowPreset ?? (presetSnapshotId.value !== null ? Number(presetSnapshotId.value) : null);
});

function getSnapshotLabel(snapshot) {
  if (snapshot.description) {
    return snapshot.description;
  }
  if (snapshot.name) {
    return snapshot.name;
  }
  return `Snapshot ${snapshot.id}`;
}

function emitSnapshotValue(value) {
  const snapshotId = Number.parseInt(value, 10);
  const normalized = Number.isNaN(snapshotId) ? null : snapshotId;
  emit('update:modelValue', normalized);
  emit('update:snapshotId', normalized);
}

async function getSnapshots() {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.listSnapshots();

    if (response?.error) {
      throw response.error;
    }

    snapshots.value = Array.isArray(response?.result)
      ? response.result.sort((a, b) => b.id - a.id)
      : [];

    if (snapshots.value.length === 0) {
      emitSnapshotValue(null);
      return;
    }

    // Use presetSnapshotId if provided and exists in the list
    const presetId = presetSnapshotId.value != null ? Number(presetSnapshotId.value)
      : (window.__appStartPresetSnapshotId != null ? Number(window.__appStartPresetSnapshotId) : null);
    if (presetId !== null && snapshots.value.some((snapshot) => Number(snapshot.id) === presetId)) {
      emitSnapshotValue(presetId);
      return;
    }

    const currentId = Number.parseInt(props.modelValue, 10);
    const hasCurrentSelection = snapshots.value.some((snapshot) => Number(snapshot.id) === currentId);
    if (!hasCurrentSelection) {
      emitSnapshotValue(snapshots.value[0].id);
    } else {
      emit('update:snapshotId', currentId);
    }
  } catch (error) {
    console.error('Error fetching snapshots:', error);
    snapshots.value = [];
    emitSnapshotValue(null);
  }
}

function onSnapshotSelected(value) {
  emitSnapshotValue(value);
}

onMounted(async () => {
  await getSnapshots();
});
</script>

<style scoped>
label {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
}

.help-logo {
  display: inline-flex;
  flex-shrink: 0;
}
</style>
